// Sprint 11b.2 — Workspace avatar deterministic defaults (D-S11b.2-02).
//
// Pure functions, no React, no DOM. Both `WorkspaceAvatar` and
// `EditProjectModal` import the same module so the live preview in the
// modal matches the rendered avatar exactly when the user clicks
// "Reset to auto".

// ────────────────────────────────────────────────────────────────────
// Default initials — runs in the frontend at render time when
// `project.avatarInitials` is null.
//
// Steps (mirrors §2.5 of sprint-11b_2 spec):
//   1. NFD normalize the name + strip combining marks (handles `Café`).
//   2. Split by whitespace; keep only words containing alphanumerics
//      (handles `🚀 Rocket` — emoji-only word is dropped).
//   3. ≥ 2 alnum-bearing words → first alnum char of words[0] + words[1].
//   4. 1 word with ≥ 2 alnum chars → first 2 alnum chars.
//   5. 1 word with 1 alnum char → pad with the first DIFFERENT alnum
//      char of the slug; if slug yields none, repeat the letter.
//   6. Fallback (name yields zero alnum) → first 2 alnum chars of slug,
//      padded with `X` if the slug also yields fewer than 2.
// ────────────────────────────────────────────────────────────────────

function stripDiacritics(s: string): string {
  // Combining diacritics block U+0300 — U+036F. Explicit codepoint
  // escape so the source file stays ASCII-safe and grep-friendly.
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function alnumChars(s: string): string {
  return s.replace(/[^a-zA-Z0-9]/g, '');
}

function firstAlnum(s: string): string {
  const m = s.match(/[a-zA-Z0-9]/);
  return m ? m[0] : '';
}

export function defaultInitials(name: string, slug: string): string {
  const normalized = stripDiacritics(name ?? '');
  const words = normalized
    .split(/\s+/)
    .filter((w) => /[a-zA-Z0-9]/.test(w));

  const w0 = words[0];
  const w1 = words[1];
  if (w0 !== undefined && w1 !== undefined) {
    return (firstAlnum(w0) + firstAlnum(w1)).toUpperCase();
  }

  const slugAlnum = alnumChars(slug ?? '').toUpperCase();

  if (w0 !== undefined) {
    const wordAlnum = alnumChars(w0);
    if (wordAlnum.length >= 2) {
      return wordAlnum.slice(0, 2).toUpperCase();
    }
    if (wordAlnum.length === 1) {
      const letter = wordAlnum.toUpperCase();
      // Take the first slug-alnum char that is NOT this letter so the
      // pair is visually distinguishable. If none remain, repeat.
      const otherSlugChar = slugAlnum
        .split('')
        .find((c) => c !== letter);
      return (letter + (otherSlugChar ?? letter)).toUpperCase();
    }
  }

  // Name had zero alnum — fall back to slug, padding with X if too short.
  return (slugAlnum + 'XX').slice(0, 2);
}

// ────────────────────────────────────────────────────────────────────
// Auto color — FNV-1a hash → HSL hue. Mirrors the S11b.1 implementation
// previously embedded inside WorkspaceAvatar so EditProjectModal's
// "Auto" preview computes the same color the deployed component
// resolves to. Single source of truth lives here from now on.
// ────────────────────────────────────────────────────────────────────

function fnv1a(str: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = (hash * 0x01000193) >>> 0;
  }
  return hash;
}

export interface AvatarRgb {
  bg: string; // CSS color value usable as `backgroundColor`
  fg: string; // text color (always white for contrast — see §2.6)
}

export function autoColor(slug: string): AvatarRgb {
  const hue = fnv1a(slug ?? '') % 360;
  return {
    bg: `hsl(${hue} var(--avatar-auto-sat) var(--avatar-auto-light))`,
    fg: 'var(--avatar-fg)',
  };
}

// ────────────────────────────────────────────────────────────────────
// Palette — DS.4 (sprint aos-ds-skin-asoasis, spec §2 D10). Returns skin
// ROLE NAMES (`var(--avatar-NN)`), not hex literals — the skin
// (web/src/styles/skin-asoasis.css) is the single declaration site for
// these 12 hex values now; `web/tailwind.config.js`'s
// `colors.avatar['palette-NN']` reads the same `var(--avatar-NN)` names, so
// exactly ONE name set exists for the palette (D10 retires
// `docs/ui/04-tokens.md` §8.5's "only intentional duplication" paragraph
// in the same commit). No `getComputedStyle()` needed: a `var(...)` STRING
// requires no DOM read, in either the real browser consumer or in
// jsdom/Vitest (§0.10 — the file's prior comment claiming otherwise was
// itself the thing measured false).
// ────────────────────────────────────────────────────────────────────

export const PALETTE_IDS = [
  'palette-01',
  'palette-02',
  'palette-03',
  'palette-04',
  'palette-05',
  'palette-06',
  'palette-07',
  'palette-08',
  'palette-09',
  'palette-10',
  'palette-11',
  'palette-12',
] as const;

export type PaletteId = (typeof PALETTE_IDS)[number];

const PALETTE_HEX: Record<PaletteId, string> = {
  'palette-01': 'var(--avatar-01)',
  'palette-02': 'var(--avatar-02)',
  'palette-03': 'var(--avatar-03)',
  'palette-04': 'var(--avatar-04)',
  'palette-05': 'var(--avatar-05)',
  'palette-06': 'var(--avatar-06)',
  'palette-07': 'var(--avatar-07)',
  'palette-08': 'var(--avatar-08)',
  'palette-09': 'var(--avatar-09)',
  'palette-10': 'var(--avatar-10)',
  'palette-11': 'var(--avatar-11)',
  'palette-12': 'var(--avatar-12)',
};

export function isPaletteId(value: unknown): value is PaletteId {
  return typeof value === 'string' && (PALETTE_IDS as readonly string[]).includes(value);
}

export function paletteColor(id: PaletteId): AvatarRgb {
  return { bg: PALETTE_HEX[id], fg: 'var(--avatar-fg)' };
}

// ────────────────────────────────────────────────────────────────────
// Resolve — the single API the avatar component uses. Given a project's
// stored fields (which may be null), returns the rendered initials and
// color pair. Centralizing the resolution here is what guarantees the
// modal's preview matches the eventual avatar after Save.
// ────────────────────────────────────────────────────────────────────

export interface ResolveAvatarInput {
  name: string;
  slug: string;
  avatarColor: string | null;
  avatarInitials: string | null;
}

export interface ResolvedAvatar {
  initials: string;
  bg: string;
  fg: string;
}

export function resolveAvatar(input: ResolveAvatarInput): ResolvedAvatar {
  const storedInitials = input.avatarInitials;
  const initials =
    typeof storedInitials === 'string' && storedInitials.length === 2
      ? storedInitials
      : defaultInitials(input.name, input.slug);
  const color = isPaletteId(input.avatarColor)
    ? paletteColor(input.avatarColor)
    : autoColor(input.slug);
  return { initials, bg: color.bg, fg: color.fg };
}
